import { AppWindow, KeyRound, Network, RefreshCw, ShieldCheck, UserRoundSearch, Users, UsersRound } from 'lucide-react'

import { defineArea, defineModule } from '@/lib/module-routes'
import { IAM_PERMISSIONS, OAUTH_APPLICATION_PERMISSIONS, SERVICE_ACCOUNT_PERMISSIONS } from '@/lib/permissions'

import { accessRoute } from './access-route'
import { iamGuardRoute } from './iam-guard-route'
import { userManagementScopeRoute } from './user-management-scope-route'
import { entityHierarchyRoute } from './routes'
import { entityGroupsRoute } from './routes'
import { groupDetailRoute } from './routes'
import { newGroupRoute } from './routes'
import { entityRolesRoute } from './routes'
import { roleDetailRoute } from './routes'
import { entityPendingUsersRoute } from './routes'
import { entityUsersRoute } from './routes'
import { provisioningRoute } from './routes'
import { importUsersRoute } from './routes'
import { newServiceAccountRoute, serviceAccountDetailRoute } from './routes'
import { serviceAccountsRoute } from './routes'
import { userAccessProfileRoute } from './routes'
import { newOAuthApplicationRoute, oauthApplicationDetailRoute, oauthApplicationsRoute } from './routes'

const accessArea = defineArea({
  id: 'access',
  label: 'Access and security sections',
  product: 'admin',
  gated: true,
  route: accessRoute,
})

declare module '@/lib/module-routes' {
  interface Areas {
    access: typeof accessArea
  }
}

export const iamModule = defineModule({
  routes: [
    iamGuardRoute,
    accessRoute,
    userManagementScopeRoute,
    entityUsersRoute,
    provisioningRoute,
    importUsersRoute,
    entityPendingUsersRoute,
    userAccessProfileRoute,
    serviceAccountsRoute,
    newServiceAccountRoute,
    serviceAccountDetailRoute,
    oauthApplicationsRoute,
    newOAuthApplicationRoute,
    oauthApplicationDetailRoute,
    entityRolesRoute,
    roleDetailRoute,
    entityGroupsRoute,
    newGroupRoute,
    groupDetailRoute,
    entityHierarchyRoute,
  ],
  areas: [accessArea],
  sections: {
    admin: [
      { id: 'access', label: 'Access', order: 10 },
      { id: 'configure', label: 'Configure', order: 30 },
    ],
  },
  navigation: {
    admin: [
      {
        to: '/$entityId/users',
        label: 'Users',
        icon: Users,
        permission: IAM_PERMISSIONS.manage,
        section: 'access',
        order: 10,
      },
      {
        to: '/$entityId/pending-users',
        label: 'Pending',
        icon: UserRoundSearch,
        permission: IAM_PERMISSIONS.manage,
        section: 'access',
        order: 30,
      },
      {
        to: '/$entityId/roles',
        label: 'Roles',
        icon: ShieldCheck,
        permission: IAM_PERMISSIONS.manage,
        section: 'access',
        order: 40,
      },
      {
        to: '/$entityId/groups',
        label: 'Groups',
        icon: UsersRound,
        permission: IAM_PERMISSIONS.manage,
        section: 'access',
        order: 50,
      },
      {
        to: '/$entityId/hierarchy',
        label: 'Hierarchy',
        icon: Network,
        permission: IAM_PERMISSIONS.manage,
        section: 'access',
        order: 60,
      },
      {
        to: '/$entityId/provisioning',
        label: 'Provisioning',
        icon: RefreshCw,
        permission: IAM_PERMISSIONS.manage,
        section: 'configure',
        order: 20,
      },
      {
        to: '/$entityId/service-accounts',
        label: 'Service Accounts',
        icon: KeyRound,
        permission: SERVICE_ACCOUNT_PERMISSIONS.read,
        section: 'configure',
        order: 10,
      },
      {
        to: '/$entityId/oauth-applications',
        label: 'OAuth applications',
        icon: AppWindow,
        permission: OAUTH_APPLICATION_PERMISSIONS.read,
        section: 'configure',
        order: 15,
      },
    ],
  },
})
